import { validate as isUUID } from 'uuid'
import { cn } from '@/utils'
import { ImageIcon } from '@radix-ui/react-icons'
import { CubeIcon } from '@heroicons/react/24/outline'
import { TemplatePreviewActionsDropdown } from './TemplatePreviewActionsDropdown'
import { ApplyThemeToOperationDialog } from './ApplyThemeToOperationDialog'
import type {
  TemplateAttribute,
  TemplateAttributeTypes,
} from '@/lib/api/client'

import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'

export const ThemePreview = ({
  className,
  documentPageId,
  documentPageThemeId,
  hideControls,
  themeTemplateAttributes,
  themeName,
  isReadOnly,
  isBuiltIn,
  isDefault,
}: {
  className?: string
  documentPageId?: string | null
  documentPageThemeId?: string | null
  hideControls?: boolean
  themeName?: string | null
  themeTemplateAttributes: TemplateAttribute[]
  isReadOnly: boolean
  isBuiltIn?: boolean
  isDefault?: boolean
}) => {
  const { relativeToAbsolutePosition } = useRelativeToAbsolutePositioning()

  const images = themeTemplateAttributes.filter(
    (tmpl) => tmpl.data_type === 'view' || tmpl.data_type === 'image',
  )

  const tables = themeTemplateAttributes.filter(
    (tmpl) => tmpl.data_type === 'table' && !tmpl.template_values.removed,
  ) as Array<any>

  const notes = themeTemplateAttributes.filter(
    (tmpl) => tmpl.data_type === 'note',
  )

  const zoom = 0.28

  return (
    <div className="w-full">
      {!hideControls && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {themeName ? (
              <h3 className="text-sm">{themeName}</h3>
            ) : (
              <span></span>
            )}
            {isDefault && (
              <span className="border border-gray-400 bg-gray-100 rounded-full px-2 text-xs text-gray-700 font-semibold">
                Default
              </span>
            )}
          </div>
          {!isReadOnly && (
            <TemplatePreviewActionsDropdown
              documentPageId={documentPageId}
              documentPageThemeId={documentPageThemeId}
              themeName={themeName}
              isBuiltIn={isBuiltIn}
            />
          )}
        </div>
      )}

      <ApplyThemeToOperationDialog
        themeTemplateAttributes={themeTemplateAttributes}
        isReadOnly={isReadOnly || !isUUID(documentPageId)}
      >
        <div
          className={cn(
            'border border-gray-300 h-48 w-full p-2 rounded-md',
            className,
            {
              'hover:border-primary-30': !isReadOnly && isUUID(documentPageId),
            },
          )}
        >
          <div className="flex flex-col flex-1 w-full relative">
            {tables.map((t, i) => (
              <div
                key={i}
                style={{
                  top: (t.template_values.position?.y || 0) * zoom,
                  left: Math.min(
                    (t.template_values.position?.x || 0) * zoom,
                    120,
                  ),
                  width: 90,
                  position: 'absolute',
                }}
                data-testid="table"
              >
                <TablePreview numCols={3} numRows={2} />
              </div>
            ))}

            {images.map((t, i) => {
              const isRelative = t.template_values.imagePosition === 'relative'
              const position = isRelative
                ? relativeToAbsolutePosition(t)
                : t.template_values.position

              return (
                <div
                  key={i}
                  className="flex flex-col justify-between top-0 left-0 flex-1 absolute"
                >
                  <div
                    style={{
                      top:
                        (position === null || isNaN(position?.y)
                          ? 50
                          : position.y) * 0.33,
                      left:
                        (position === null || isNaN(position?.x)
                          ? 350 * i
                          : position.x) * zoom,
                      // @ts-expect-error - template value type is incorrect
                      width: (t.template_values?.size?.width || 320) * 0.3,
                      // @ts-expect-error - template value type is incorrect
                      height: (t.template_values?.size?.height || 150) * 0.3,
                      position: 'absolute',
                    }}
                  >
                    <ImagePreview dataType={t.data_type} />
                  </div>
                </div>
              )
            })}

            {notes.map((t, i) => {
              const isRelative = t.template_values.notesPosition === 'relative'
              const position = isRelative
                ? relativeToAbsolutePosition(t)
                : t.template_values.position

              return (
                <div
                  key={i}
                  style={{
                    top:
                      (position === null || isNaN(position?.y)
                        ? 100
                        : position.y) * 0.33,
                    left:
                      (position === null || isNaN(position?.x)
                        ? 10 * i
                        : position.x) * zoom,
                    // @ts-expect-error - template value type is incorrect
                    width: (t.template_values?.size?.width || 320) * 0.3,
                    // @ts-expect-error - template value type is incorrect
                    height: (t.template_values?.size?.height || 150) * 0.3,
                    position: 'absolute',
                  }}
                >
                  <TextPreview
                    numLines={parseInt(
                      // @ts-expect-error - template value type is incorrect
                      (t.template_values?.size?.height || 150) / 40,
                    )}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </ApplyThemeToOperationDialog>
    </div>
  )
}

const TablePreview = ({
  numCols,
  numRows,
}: {
  numCols: number
  numRows: number
}) => {
  const rows = Array.from({ length: numRows }).map(() =>
    Array.from({ length: numCols }),
  )

  return (
    <div className="w-full border border-gray-100 rounded-sm shadow-sm overflow-hidden">
      {/* Header row */}
      <div
        className="grid gap-y-1 gap-x-1 bg-gray-50 p-0.5"
        style={{ gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: numCols }).map((_, idx) => (
          <div key={idx} className="h-2 bg-gray-200"></div>
        ))}
      </div>

      {/* Body rows */}
      <div className="space-y-2 p-0.5">
        {rows.slice(1).map((_, rowIdx) => (
          <div
            key={rowIdx}
            className="grid gap-y-1 gap-x-1"
            style={{
              gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: numCols }).map((__, colIdx) => (
              <div key={colIdx} className="h-2 bg-gray-200"></div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const ImagePreview = ({ dataType }: { dataType: TemplateAttributeTypes }) => {
  return (
    <div className="border border-gray-300 bg-gray-100 h-full w-full flex place-items-center justify-center rounded-sm">
      {dataType === 'view' ? (
        <CubeIcon className="h-4 w-4 text-gray-400" />
      ) : (
        <ImageIcon className="h-4 w-4 text-gray-400" />
      )}
    </div>
  )
}

const TextPreview = ({ numLines }: { numLines: number }) => {
  const lines = Array.from({ length: numLines })

  return (
    <div className="w-full flex flex-col space-y-1">
      {lines.map((_, i) => (
        <div
          key={i}
          className={cn('h-1.5 bg-gray-100 rounded-full', {
            'w-full': i !== lines.length - 1,
            'w-1/2': i === lines.length - 1,
          })}
        ></div>
      ))}
    </div>
  )
}
